import { DEFAULT_PING_INTERVAL, DEFAULT_PING_TIMEOUT } from "@/constants";
import { API } from "@/lib/api";

interface NetworkDetectorOptions {
  /**
   * The endpoint URL to ping for connectivity checks.
   * If not provided, only browser online/offline events will be used.
   */
  pingEndpoint?: string;

  /**
   * How often to perform automatic connectivity checks in milliseconds.
   * @default 30000 (30 seconds)
   */
  pingInterval?: number;

  /**
   * Maximum time to wait for a ping response before considering it failed, in milliseconds.
   * @default 5000 (5 seconds)
   */
  pingTimeout?: number;
}

interface NetworkStatus {
  isOnline: boolean;
}

type NetworkEventType = "online" | "offline" | "status-change";
type NetworkEventCallback = (status: NetworkStatus) => void;

/**
 * NetworkDetector provides comprehensive network connectivity monitoring
 * with real-time status updates and event-driven architecture.
 *
 * Features:
 * - Real-time connectivity monitoring
 * - Automatic retry with fixed delay
 * - Event-driven status updates
 * - Browser focus detection for immediate connectivity checks
 * - Tab visibility detection for connectivity checks
 * - Graceful cleanup and resource management
 */
export class NetworkDetectorService {
  private isOnline: boolean;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private currentController: AbortController | null = null;
  private eventListeners: Map<NetworkEventType, Set<NetworkEventCallback>>;
  private listenersSetup = false;
  private activeCheck: Promise<boolean> | null = null;

  private readonly pingEndpoint?: string;
  private readonly pingInterval: number;
  private readonly pingTimeout: number;

  constructor(options: NetworkDetectorOptions = {}) {
    this.pingEndpoint = options.pingEndpoint ?? API.HealthCheck;
    this.pingInterval = options.pingInterval ?? DEFAULT_PING_INTERVAL;
    this.pingTimeout = options.pingTimeout ?? DEFAULT_PING_TIMEOUT;
    this.isOnline = navigator.onLine;
    this.eventListeners = new Map([
      ["online", new Set()],
      ["offline", new Set()],
      ["status-change", new Set()],
    ]);
  }

  private setupEventListeners(): void {
    if (this.listenersSetup) return;

    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
    window.addEventListener("focus", this.handleFocus);

    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    this.listenersSetup = true;
  }

  private async checkConnectivity(): Promise<boolean> {
    if (!this.pingEndpoint) {
      return navigator.onLine;
    }

    // If there's already an active check, return the same promise
    if (this.activeCheck) {
      return this.activeCheck;
    }

    // Start a new check and store the promise
    this.activeCheck = this.pingServer();

    try {
      return await this.activeCheck;
    } finally {
      this.activeCheck = null;
    }
  }

  private handleOnline = async (): Promise<void> => {
    const isOnline = await this.checkConnectivity();
    this.updateStatus(isOnline);
  };

  private handleOffline = (): void => {
    this.updateStatus(false);
  };

  private handleFocus = async (): Promise<void> => {
    if (navigator.onLine) {
      const isConnected = await this.checkConnectivity();
      this.updateStatus(isConnected);
    }
  };

  private handleVisibilityChange = async (): Promise<void> => {
    if (!document.hidden && navigator.onLine) {
      const isConnected = await this.checkConnectivity();
      this.updateStatus(isConnected);
    }
  };

  private async pingServer(): Promise<boolean> {
    if (!this.pingEndpoint) {
      return false;
    }

    if (this.currentController) {
      this.currentController.abort();
    }

    this.currentController = new AbortController();

    let timeoutId: NodeJS.Timeout | undefined;

    try {
      timeoutId = setTimeout(() => {
        this.currentController?.abort();
      }, this.pingTimeout);

      const response = await fetch(this.pingEndpoint, {
        method: "GET", // TODO: should be HEAD: https://asm-redcurrant.atlassian.net/browse/AR-564
        cache: "no-cache",
        signal: this.currentController.signal,
      });

      return response.ok;
    } catch (error) {
      // Ignore aborted requests (don't treat them as offline)
      if (error instanceof Error && error.name === "AbortError") {
        // Return current status instead of false for cancelled requests
        return this.isOnline;
      }
      // All other errors (network, timeout) result in offline status
      return false;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(async () => {
      if (navigator.onLine) {
        const isConnected = await this.checkConnectivity();
        this.updateStatus(isConnected);
      }
    }, this.pingInterval);
  }

  private updateStatus(online: boolean): void {
    const statusChanged = this.isOnline !== online;
    this.isOnline = online;

    const status: NetworkStatus = {
      isOnline: online,
    };

    if (statusChanged) {
      const eventType: NetworkEventType = online ? "online" : "offline";
      this.emit(eventType, status);
    }

    this.emit("status-change", status);
  }

  private emit(eventType: NetworkEventType, status: NetworkStatus): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(status);
        } catch (error) {
          console.error(
            `Error in network event listener (${eventType}):`,
            error,
          );
        }
      });
    }
  }

  /**
   * Get the current network status without performing a new check
   */
  public getStatus(): NetworkStatus {
    return {
      isOnline: this.isOnline,
    };
  }

  /**
   * Perform an immediate network connectivity check
   */
  public async checkNow(): Promise<NetworkStatus> {
    const isConnected = await this.checkConnectivity();
    const status: NetworkStatus = {
      isOnline: isConnected,
    };

    this.updateStatus(isConnected);
    return status;
  }

  /**
   * Add an event listener for network status changes
   */
  public on(eventType: NetworkEventType, callback: NetworkEventCallback): void {
    const listeners = this.eventListeners.get(eventType);
    listeners?.add(callback);
  }

  /**
   * Remove an event listener for network status changes
   */
  public off(
    eventType: NetworkEventType,
    callback: NetworkEventCallback,
  ): void {
    const listeners = this.eventListeners.get(eventType);
    listeners?.delete(callback);
  }

  /**
   * Get the number of active listeners for a specific event type
   */
  public getListenerCount(eventType: NetworkEventType): number {
    const listeners = this.eventListeners.get(eventType);
    return listeners?.size ?? 0;
  }

  /**
   * Check if the detector is currently running
   */
  public isRunning(): boolean {
    return this.heartbeatInterval !== null;
  }

  /**
   * Stop the heartbeat monitoring (can be restarted with start())
   */
  public stop(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    this.removeEventListeners();
  }

  private removeEventListeners(): void {
    if (!this.listenersSetup) return;

    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
    window.removeEventListener("focus", this.handleFocus);
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );

    this.listenersSetup = false;
  }

  /**
   * Start or restart the heartbeat monitoring
   */
  public start(): void {
    if (!this.heartbeatInterval) {
      this.setupEventListeners();
      this.startHeartbeat();
    }
  }

  /**
   * Clean up all resources and event listeners
   */
  public destroy(): void {
    this.stop();

    if (this.currentController) {
      this.currentController.abort();
      this.currentController = null;
    }

    this.eventListeners.forEach((listeners) => listeners.clear());
  }
}

export type {
  NetworkDetectorOptions,
  NetworkStatus,
  NetworkEventType,
  NetworkEventCallback,
};

export const NetworkDetector = new NetworkDetectorService();
